import { css } from '@emotion/css';
import * as d3 from 'd3';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { Color, Font } from 'src/styles';

export const gridStyle = css`
  fill: none;
  stroke: rgba(189, 189, 189);
  stroke-width: 1px;
  path {
    stroke-dasharray: 2 2;
    opacity: 0.3;
  }
  line {
    stroke-dasharray: 2 2;
    opacity: 0.3;
  }
  text {
    font-family: 'Inter';
    font-style: normal;
    font-weight: 100;
    ${Font.xxs}
    stroke-dasharray: none;
    stroke: ${Color.Gray8};
  }
`;

export interface ChartOptions {
  xAxis?: {
    tickInterval?: d3.CountableTimeInterval;
    tickStep?: number;
    tickFormat?: string;
  };
}

const selectTickIndices = (sortedData: [number, number][], options?: ChartOptions): number[] => {
  const len = sortedData.length;
  if (len <= 1) return len === 1 ? [0] : [];

  const interval = options?.xAxis?.tickInterval;
  const step = options?.xAxis?.tickStep || 1;

  if (interval) {
    const indices: number[] = [];
    let nextBoundary = interval.ceil(new Date(sortedData[0][0]));

    for (let i = 0; i < len; i++) {
      const date = new Date(sortedData[i][0]);

      if (date >= nextBoundary) {
        indices.push(i);
        let advanced = nextBoundary;

        for (let s = 0; s < step; s++) {
          advanced = interval.offset(advanced, 1);
        }
        nextBoundary = advanced;
      }
    }

    if (indices.length === 0) {
      indices.push(0);
      if (len > 1) indices.push(len - 1);
    }

    return indices;
  }

  const maxTicks = Math.min(8, len);
  const spacing = (len - 1) / (maxTicks - 1);

  return Array.from({ length: maxTicks }, (_, i) => Math.round(i * spacing));
};

export const drawChart = (
  element: HTMLElement,
  data: [number, number][], // [timestamp, price]
  width: number,
  height: number,
  options?: ChartOptions,
) => {
  d3.select(element).select('svg').remove();
  d3.select(element).select('.tooltip').remove();

  if (isEmpty(data)) {
    // Create SVG for "No data" message
    const svg = d3.select(element).append('svg').attr('width', width).attr('height', height);

    svg
      .append('text')
      .attr('x', width / 2)
      .attr('y', height / 2)
      .attr('text-anchor', 'middle')
      .attr('dominant-baseline', 'middle')
      .style('font-family', 'Inter')
      .style('font-size', '14px')
      .style('fill', Color.Gray8)
      .text('No data available');

    return;
  }

  const margin = { top: 16, right: 20, bottom: 60, left: 55 };
  const chartWidth = width - margin.left - margin.right;
  const chartHeight = height - margin.top - margin.bottom;

  const yMaxValue = d3.max(data, d => d[1]) as number;
  const yMinValue = d3.min(data, d => d[1]) as number;

  // Add 5% padding to Y axis for better visualization
  const yPadding = (yMaxValue - yMinValue) * 0.05;
  const yMin = yMinValue - yPadding;
  const yMax = yMaxValue + yPadding;

  // Remove old svg and tooltip
  d3.select(element).select('svg').remove();
  d3.select(element).select('.tooltip').remove();

  d3.select(element).style('position', 'relative');

  // Create SVG
  const svg = d3
    .select(element)
    .append('svg')
    .attr('width', chartWidth + margin.left + margin.right)
    .attr('height', chartHeight + margin.top + margin.bottom)
    .append('g')
    .attr('transform', `translate(${margin.left}, ${margin.top})`);

  // Index-based x scale so only actual data points appear (no weekend/holiday gaps)
  const xScale = d3
    .scaleLinear()
    .domain([0, data.length - 1])
    .range([0, chartWidth]);

  const yScale = d3.scaleLinear().domain([yMin, yMax]).range([chartHeight, 0]);

  const tickFormat = options?.xAxis?.tickFormat || 'HH:mm';

  const rawTickIndices = selectTickIndices(data, options);

  const tickIndices = rawTickIndices.filter((idx, i) => {
    if (i === 0) return true;
    const prevLabel = moment(new Date(data[rawTickIndices[i - 1]][0])).format(tickFormat);
    const currLabel = moment(new Date(data[idx][0])).format(tickFormat);

    return currLabel !== prevLabel;
  });

  const MIN_TICK_SPACING_PX = 50;
  const spacedTickIndices = tickIndices.filter((idx, i) => {
    if (i === 0) return true;
    const prevX = xScale(tickIndices[i - 1]);
    const currX = xScale(idx);

    return currX - prevX >= MIN_TICK_SPACING_PX;
  });

  // X-axis grid
  svg
    .append('g')
    .attr('class', gridStyle)
    .attr('transform', `translate(0,${chartHeight})`)
    .call(
      d3
        .axisBottom(xScale)
        .tickValues(spacedTickIndices)
        .tickSize(-chartHeight)
        .tickPadding(10)
        .tickFormat((_d, i) => moment(new Date(data[spacedTickIndices[i]][0])).format(tickFormat)),
    );

  // Y-axis grid
  svg
    .append('g')
    .attr('class', gridStyle)
    .call(
      d3
        .axisLeft(yScale)
        .ticks(6)
        .tickSize(-chartWidth)
        .tickPadding(10)
        .tickFormat(d => `$${Number(d).toFixed(2)}`),
    );

  // Create gradient
  const defs = svg.append('defs');
  const gradient = defs
    .append('linearGradient')
    .attr('id', 'svgGradient')
    .attr('x1', '0%')
    .attr('x2', '0%')
    .attr('y1', '0%')
    .attr('y2', '100%');

  gradient.append('stop').attr('offset', '0%').attr('stop-color', 'rgba(2, 85, 163, 0.47)');

  gradient.append('stop').attr('offset', '100%').attr('stop-color', 'rgba(255, 255, 255, 0)');

  // Create area generator (index-based x)
  const area = d3
    .area<[number, number]>()
    .x((_d, i) => xScale(i))
    .y0(chartHeight)
    .y1(d => yScale(d[1]))
    .curve(d3.curveMonotoneX);

  // Create line generator (index-based x)
  const line = d3
    .line<[number, number]>()
    .x((_d, i) => xScale(i))
    .y(d => yScale(d[1]))
    .curve(d3.curveMonotoneX);

  // Draw area
  svg.append('path').datum(data).attr('fill', 'url(#svgGradient)').attr('class', 'area').attr('d', area);

  // Draw line
  svg
    .append('path')
    .datum(data)
    .attr('fill', 'none')
    .attr('stroke', '#0255A3')
    .attr('stroke-width', 2)
    .attr('class', 'line')
    .attr('d', line);

  // Create tooltip
  const tooltip = d3
    .select(element)
    .append('div')
    .attr('class', 'tooltip')
    .style('position', 'absolute')
    .style('visibility', 'hidden')
    .style('background-color', 'black')
    .style('color', 'white')
    .style('border', '1px solid #ddd')
    .style('border-radius', '4px')
    .style('padding', '8px')
    .style('font-size', '12px')
    .style('pointer-events', 'none')
    .style('z-index', '1000')
    .style('box-shadow', '0 2px 4px rgba(0,0,0,0.1)');

  const tooltipArea = svg
    .append('rect')
    .attr('width', chartWidth)
    .attr('height', chartHeight)
    .style('fill', 'none')
    .style('pointer-events', 'all');

  const focusCircle = svg
    .append('circle')
    .attr('r', 5)
    .attr('fill', '#0255A3')
    .attr('stroke', 'white')
    .attr('stroke-width', 2)
    .style('opacity', 0);

  tooltipArea
    .on('mouseover', () => {
      tooltip.style('visibility', 'visible');
      focusCircle.style('opacity', 1);
    })
    .on('mousemove', function (event) {
      const [mouseX] = d3.pointer(event, this);

      const hoveredIndex = xScale.invert(mouseX);
      const index = Math.max(0, Math.min(data.length - 1, Math.round(hoveredIndex)));
      const d = data[index];

      focusCircle.attr('cx', xScale(index)).attr('cy', yScale(d[1]));

      tooltip.html(`
      <div><strong>Date:</strong> ${moment(d[0]).format('MMM D, YYYY HH:mm')}</div>
      <div><strong>Price:</strong> $${d[1].toFixed(2)}</div>
    `);

      tooltip.style('top', `${event.offsetY - 30}px`).style('left', `${event.offsetX + 10}px`);
    })
    .on('mouseout', () => {
      tooltip.style('visibility', 'hidden');
      focusCircle.style('opacity', 0);
    });
};
